import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Info, Tv, Bookmark, BookmarkCheck, Shield, Users, Film } from "lucide-react";
import { api, titleUrl, fmtRuntime } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useAuth } from "@/context/AuthContext";
import { Row } from "@/components/Row";
import { SearchBox } from "@/components/SearchBox";
import { VideoCard, VideoPlayer } from "@/components/VideoCard";
import { useTitleActions, TrailerModal } from "@/components/TitleActions";
import { Loading, ErrorBox, Empty, Avatar, Spoiler } from "@/components/Common";
import { Stars } from "@/components/StarInput";

function Hero({ t, note }) {
  const { state, toggle } = useTitleActions(t);
  const [trailer, setTrailer] = useState(null);
  const verdict = t.verdict?.label || note;
  return (
    <section data-testid="featured-hero" className="grain relative mx-4 mt-4 flex min-h-[78vh] items-end overflow-hidden rounded-3xl border border-white/5 sm:mx-6 lg:mx-12 lg:min-h-[72vh]">
      <img src={t.backdrop || t.poster} alt={`${t.title} backdrop`} className="absolute inset-0 h-full w-full object-cover" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-[#0A0A0E]/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0E]/90 via-[#0A0A0E]/30 to-transparent" />
      <div className="relative z-10 w-full max-w-3xl p-6 sm:p-10 lg:p-14 fade-up">
        <p className="eyebrow mb-3">Featured pick</p>
        <h1 className="font-display text-5xl leading-[0.9] text-white sm:text-6xl lg:text-8xl">{t.title}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-300">
          <span>{t.year}</span><span className="text-slate-600">•</span>
          {t.runtime > 0 && <><span>{fmtRuntime(t.runtime)}{t.type !== "movie" ? "/ep" : ""}</span><span className="text-slate-600">•</span></>}
          {t.genres?.slice(0, 3).map((g) => <span key={g} className="rounded-full border border-white/15 px-2.5 py-0.5 text-xs">{g}</span>)}
        </div>
        <p className="mt-4 line-clamp-3 max-w-xl text-sm text-slate-300 sm:text-base">{t.synopsis}</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <span className="rounded-md bg-[#F5C518]/10 px-2.5 py-1 font-semibold text-[#F5C518]">IMDb {t.imdb_rating ? `${t.imdb_rating}/10` : "rating unavailable"}</span>
          {verdict && <span className="flex items-center gap-1 rounded-md bg-amber-400/10 px-2.5 py-1 font-semibold text-amber-300"><Shield className="h-3.5 w-3.5" />Shivam's Verdict: {verdict}</span>}
        </div>
        <div className="mt-7 flex flex-wrap gap-3">
          {t.trailer_key && <button data-testid="hero-watch-trailer" onClick={() => setTrailer(t.trailer_key)} className="flex items-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-sm font-bold text-black hover:bg-amber-300 transition-colors"><Play className="h-4 w-4 fill-black" />Watch Trailer</button>}
          <Link data-testid="hero-explore" to={titleUrl(t)} className="flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold backdrop-blur hover:border-white/40"><Info className="h-4 w-4" />Explore</Link>
          <Link data-testid="hero-where-to-watch" to={`${titleUrl(t)}#where-to-watch`} className="flex items-center gap-2 rounded-full border border-purple-400/40 bg-purple-500/10 px-6 py-3 text-sm font-semibold text-purple-200 hover:bg-purple-500/20"><Tv className="h-4 w-4" />Where to Watch</Link>
          <button data-testid="hero-watchlist" onClick={() => toggle("watchlist")} aria-label="Add to watchlist" className="rounded-full border border-white/20 p-3 hover:border-amber-400 hover:text-amber-400">{state.watchlist ? <BookmarkCheck className="h-5 w-5 text-amber-400" /> : <Bookmark className="h-5 w-5" />}</button>
        </div>
      </div>
      <TrailerModal trailerKey={trailer} onClose={() => setTrailer(null)} />
    </section>
  );
}

function Videos({ videos, settings }) {
  const [playing, setPlaying] = useState(null);
  return (
    <section data-testid="videos-section" className="relative my-10 overflow-hidden border-y border-white/5 bg-gradient-to-br from-purple-950/40 via-[#0A0A0E] to-[#0A0A0E] py-14">
      <div className="grid gap-10 px-4 sm:px-6 lg:grid-cols-[320px_1fr] lg:px-12">
        <div className="flex flex-col items-start">
          <img src={settings.portrait_url || settings.logo_url} alt="Shivam, creator of Shivam Cine Core" loading="lazy" className="h-32 w-32 rounded-full object-cover ring-2 ring-amber-400/60" />
          <p className="eyebrow mt-6">From the creator</p>
          <h2 className="font-display text-5xl leading-none">Watch, Discover, Explore</h2>
          <p className="mt-3 text-sm text-slate-400">Movie-explained videos, Shorts and Reels — each one linked to its movie page so you can rate it, save it and find where to watch it legally.</p>
        </div>
        {videos.length ? (
          <div className="no-scrollbar flex snap-x gap-4 overflow-x-auto pb-2">
            {videos.map((v) => <VideoCard key={v.id} v={v} onPlay={setPlaying} />)}
          </div>
        ) : <Empty icon={Film} title="Videos are on the way" text="Shivam's YouTube videos, Shorts and Reels will appear here as soon as they're added." testid="videos-empty" />}
      </div>
      <VideoPlayer v={playing} onClose={() => setPlaying(null)} />
    </section>
  );
}

function Activity() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/community/activity").then((r) => setA(r.data)).catch(() => setA({ reviews: [], posts: [], collections: [] })); }, []);
  if (!a) return null;
  const empty = !a.reviews.length && !a.posts.length && !a.collections.length;
  return (
    <section data-testid="community-activity" className="px-4 py-10 sm:px-6 lg:px-12">
      <p className="eyebrow">Community</p>
      <h2 className="mb-6 font-display text-4xl">What members are saying</h2>
      {empty ? (
        <Empty icon={Users} title="Be the first voice here" text="Our community is just getting started. Rate a movie, write a review or start a discussion — your opinion could be the first one people read."
          action={<Link to="/register" className="rounded-full bg-amber-400 px-6 py-3 text-sm font-bold text-black">Join the community</Link>} testid="community-empty" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {a.reviews.map((r) => (
            <Link key={r.id} to={r.movie ? titleUrl(r.movie) : "#"} className="rounded-2xl border border-white/5 bg-[#12121A] p-5 hover:border-purple-400/30 transition-colors">
              <div className="flex items-center gap-2 text-sm"><Avatar user={r.user} size={28} /><span className="font-semibold">@{r.user.username}</span><span className="text-slate-500">reviewed</span><span className="text-amber-400 line-clamp-1">{r.movie?.title}</span></div>
              <Spoiler spoiler={r.spoiler} id={r.id}><p className="mt-3 line-clamp-3 text-sm text-slate-300">{r.body}</p></Spoiler>
            </Link>
          ))}
          {a.posts.map((p) => (
            <Link key={p.id} to={`/community/post/${p.id}`} className="rounded-2xl border border-white/5 bg-[#12121A] p-5 hover:border-purple-400/30 transition-colors">
              <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300">{p.kind}</span>
              <p className="mt-1 font-semibold">{p.title}</p>
              <p className="mt-2 text-xs text-slate-500">by @{p.user.username} · {p.comments} comments</p>
            </Link>
          ))}
          {a.collections.map((c) => (
            <Link key={c.id} to={`/collection/${c.id}`} className="rounded-2xl border border-white/5 bg-[#12121A] p-5 hover:border-purple-400/30">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">Collection</span>
              <p className="mt-1 font-semibold">{c.name}</p><p className="mt-2 text-xs text-slate-500">{c.count} titles · @{c.user.username}</p>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function ForYou() {
  const { user } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => { if (user) api.get("/recommendations").then((x) => setR(x.data)).catch(() => {}); }, [user]);
  if (!user || !r?.items?.length) return null;
  return <Row testid="row-for-you" title={`Because you liked ${r.basis.slice(0, 2).join(" & ")}`} items={r.items} />;
}

export default function Home() {
  const { settings } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useSeo({ title: settings.seo_default?.title || "Shivam Cine Core", description: settings.seo_default?.description });
  const load = () => { setErr(false); api.get("/home").then((r) => setData(r.data)).catch(() => setErr(true)); };
  useEffect(load, []);
  if (err) return <ErrorBox text="We couldn't load the homepage right now." retry={load} />;
  if (!data) return <Loading />;
  return (
    <div data-testid="home-page">
      {data.featured && <Hero t={data.featured} note={data.featured_note} />}
      <section className="px-4 py-10 sm:px-6 lg:px-12">
        <div className="grid items-end gap-6 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="eyebrow">{settings.tagline}</p>
            <h2 className="font-display text-4xl leading-none sm:text-5xl">Search for your next movie</h2>
          </div>
          <SearchBox big />
        </div>
      </section>
      <ForYou />
      {data.sections.slice(0, 3).map((s) => <Row key={s.key} testid={`row-${s.key}`} title={s.title} note={s.note} items={s.items} viewAll={`/browse/${s.key}`} />)}
      <Videos videos={data.videos} settings={settings} />
      {data.sections.slice(3).map((s) => <Row key={s.key} testid={`row-${s.key}`} title={s.title} note={s.note} items={s.items} viewAll={`/browse/${s.key}`}
        empty={s.rule === "community" ? "No community ratings yet — be the first to rate a movie." : s.rule === "recent_reviewed" ? "No reviews yet. Write the first one!" : undefined} />)}
      <Activity />
    </div>
  );
}

export { Stars };
